// "Why Ahillah?" (src/ui/NamePanel.tsx and the 8 October update): the verse the name comes from, and the scholars on
// what it means, in their own words, as on "The stars in Islam" (printed books, quoted, never paraphrased). The Arabic
// and its sources are in data-src/islam/name/sources.json (the verse in the King Fahd Complex's Uthmanic text); the
// English was translated independently from the Arabic by GPT-6 Astra (data-src/islam/name/translations.json).

export interface NameQuote { lead: string; arabic: string; english: string; ref: string }

/** Al-Baqarah 2:189, its first part, as the King Fahd Complex sets it (KFGQPC Uthmanic Hafs data v18). */
const VERSE_2_189 = 'يَسۡـَٔلُونَكَ عَنِ ٱلۡأَهِلَّةِۖ قُلۡ هِيَ مَوَٰقِيتُ لِلنَّاسِ وَٱلۡحَجِّۗ';

export const WHY_NAME = {
  /** The name as it is said, under the Arabic. */
  say: 'Ahillah',
  intro: 'The app is named after the new crescents, which Allah mentions in the Quran:',
  verse: {
    arabic: VERSE_2_189,
    english: 'They ask you about the ahillah [new crescents]. Say: They are markers of time for people and for Ḥajj.',
    ref: '[Al-Baqarah: 2:189]',
    refAr: '[البقرة: ١٨٩]',
  },
  quotes: [
    {
      lead: 'Ibn ʿUthaymīn ﵀ said:',
      arabic: '{الْأَهِلَّةُ} جَمْعُ هِلَالٍ؛ وَهُوَ الْقَمَرُ أَوَّلَ مَا يَكُونُ شَهْرًا؛ وَسُمِّيَ هِلَالًا لِظُهُورِهِ',
      english: 'Al-ahillah [the new crescents] is the plural of hilāl [the new crescent]. The hilāl is the Moon as it first begins the month. It is called hilāl because it appears.',
      ref: 'Tafsīr al-Fātiḥah wal-Baqarah, 2/367',
    },
    {
      lead: 'Ibn Fāris ﵀ said, of the word hilāl:',
      arabic: '… فَالْهِلَالُ الَّذِى فِى السَّمَاءِ، سُمِّىَ بِهِ لِإِهْلَالِ النَّاسِ عِنْدَ نَظَرِهِمْ إِلَيْهِ مُكَبِّرِينَ وَدَاعِينَ. وَيُسَمَّى هِلَالًا أَوَّلَ لَيْلَةٍ وَالثَّانِيَةِ وَالثَّالِثَةِ، ثُمَّ هُوَ قَمَرٌ بَعْدَ ذَلِكَ.',
      english: 'The hilāl [the new crescent] in the sky was given this name because of people’s ihlāl [raising their voices] when they look at it, saying takbīr [‘Allah is the Greatest’] and supplicating. It is called hilāl on the first night, the second and the third. After that, it is qamar [the Moon].',
      ref: 'Muʿjam Maqāyīs al-Lughah, 6/11',
    },
    {
      lead: 'Al-Baghawī ﵀ said, explaining “markers of time” in the verse:',
      arabic: 'جَمْعُ مِيقَاتٍ أَيْ فَعَلْنَا ذَلِكَ لِيَعْلَمَ النَّاسُ أَوْقَاتَ الْحَجِّ وَالْعُمْرَةِ وَالصَّوْمِ وَالْإِفْطَارِ وَآجَالَ الدُّيُونِ وَعِدَدَ النِّسَاءِ وَغَيْرَهَا',
      english: '[Mawāqīt is] the plural of mīqāt [an appointed time]. That is, We did this so that people may know the times for Ḥajj, ʿUmrah, fasting and breaking the fast, when debts fall due, the waiting periods of women and other such matters.',
      ref: 'Maʿālim al-Tanzīl, 1/211',
    },
  ] as NameQuote[],
  /** The editions the volumes and pages refer to. */
  sources: 'Ibn ʿUthaymīn, Tafsīr al-Fātiḥah wal-Baqarah (Dār Ibn al-Jawzī, 1st ed., 1423). Ibn Fāris, Muʿjam Maqāyīs al-Lughah, ed. ʿAbd al-Salām Hārūn (Muṣṭafá al-Bābī al-Ḥalabī, 2nd ed., 1389-1392). Al-Baghawī, Maʿālim al-Tanzīl, ed. al-Nimr, Ḍumayriyyah and al-Ḥarsh (Dār Ṭaybah, 4th ed., 1417).',
  close: 'The new crescents begin every month of the Islamic year. Ahillah can tell you when Saudi Arabia looks for the crescent of Ramaḍān, Shawwāl and Dhū al-Ḥijjah, and what it announces.',
};
